import {useMutation} from '@apollo/client';
import {
  DepositoryType,
  DeleteAccountDocument,
  UpdateDepositoryAccountDocument,
  useAccountQuery,
  useBalanceAnchorsQuery,
  useDeleteBalanceAnchorMutation,
  useRecordBalanceAnchorMutation,
  useSetAccountOpeningAnchorMutation,
} from '@transactions/graphql';
import {today} from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {AccountClosingSection} from 'src/AccountClosingSection';
import {setAccounts} from 'src/apolloCache/updateAccounts';
import {Button} from 'src/Button';
import {CardProductSelect} from 'src/CardProductSelect';
import Checkbox from 'src/Checkbox/Checkbox';
import DatePicker from 'src/DatePicker/DatePicker';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {LogoSelect} from 'src/LogoSelect';
import {Modal} from 'src/Modal';
import type {ApolloCache, FetchResult} from '@apollo/client';
import type {DeleteAccountMutation} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';

// Minimal type for depository account
type DepositoryAccountForEdit = {
  _id: string;
  name: string;
  depositoryType: DepositoryType;
  logo?: string | null;
  cardProduct?: string | null;
  trackBalance?: boolean;
};

export type EditAccountModalUserProps = {
  onUpdate?: () => void;
  onDelete?: (deleted: DepositoryAccountForEdit) => void;
  account: DepositoryAccountForEdit;
};

/**
 * Set (or clear) the account's opening balance: a $0 balance anchor on its
 * inception date that attests the record is complete before it. The opener is
 * always zero — the account began empty, and the first deposit is a
 * transaction after it; an account whose early history is missing simply gets
 * no opener. Self-contained (fetches its own state and commits immediately)
 * rather than folded into the modal's deferred Save, because setting it is a
 * two-step create-then-designate that rolls back the created anchor if the
 * designation is refused.
 */
const OpeningBalanceSection = ({accountId}: {accountId: string}) => {
  const formatMoney = useMoneyFormatter();
  const {data: accountData, refetch: refetchAccount} = useAccountQuery({
    variables: {id: accountId},
    fetchPolicy: 'cache-and-network',
  });
  const account = accountData?.account;
  const currency = account?.currency ?? 'USD';
  const isCreditCard =
    account?.__typename === 'DepositoryAccount' &&
    account.depositoryType === DepositoryType.CreditCard;
  const openingAnchorId =
    account?.__typename === 'DepositoryAccount'
      ? account.openingAnchorId
      : null;

  const {data: anchorsData, refetch: refetchAnchors} = useBalanceAnchorsQuery({
    variables: {accountId},
    fetchPolicy: 'cache-and-network',
  });
  const anchors = anchorsData?.balanceAnchors ?? [];
  const openingAnchor = anchors.find((a) => a._id === openingAnchorId) ?? null;

  // Gate all controls until both queries have resolved. Opened from the
  // accounts list / side nav, the cached account entry (AccountListItem) has no
  // openingAnchorId, so cache-and-network yields no account until the network
  // lands: acting in that window would see no existing opener (clobbering an
  // unloaded one).
  const ready = account != null && anchorsData?.balanceAnchors != null;

  const [date, setDate] = React.useState<DateTime>(today());
  const [error, setError] = React.useState<string | null>(null);

  const [recordAnchor, {loading: recording}] = useRecordBalanceAnchorMutation();
  const [setOpeningAnchor, {loading: setting}] =
    useSetAccountOpeningAnchorMutation();
  const [deleteAnchor] = useDeleteBalanceAnchorMutation();
  const busy = recording || setting;

  const handleSet = async () => {
    setError(null);
    if (!ready) return;
    const iso = date.toISODate();
    if (!iso) {
      setError('Pick a valid start date');
      return;
    }
    // The opener must be the account's earliest point. Refuse a date that
    // already has an earlier anchor here (the server also rejects earlier
    // transactions and Plaid readings); surface that as a friendly message
    // before creating anything.
    const earlier = anchors.find((a) => a.date < iso);
    if (earlier) {
      setError(
        `An earlier balance exists on ${earlier.date}. The opening balance ` +
          'must be the earliest point on the account.',
      );
      return;
    }
    // recordBalanceAnchor upserts by (account, date): if an anchor already
    // exists on this date, its balance/source is overwritten. Capture the prior
    // values so a refused designation can be fully reverted — otherwise a
    // failed set would silently leave that pre-existing balance changed.
    const prior = anchors.find((a) => a.date === iso) ?? null;
    try {
      const recorded = await recordAnchor({
        variables: {accountId, date: iso, balance: 0, source: 'manual'},
      });
      const anchorId = recorded.data?.recordBalanceAnchor._id;
      if (!anchorId) {
        throw new Error('Failed to record opening balance');
      }
      try {
        await setOpeningAnchor({variables: {accountId, anchorId}});
      } catch (designationErr) {
        // Revert the write so a rejected designation changes nothing: delete a
        // freshly created anchor, or restore a pre-existing one's prior values.
        if (prior) {
          await recordAnchor({
            variables: {
              accountId,
              date: iso,
              balance: prior.balance,
              source: prior.source,
            },
          }).catch(() => undefined);
        } else {
          await deleteAnchor({variables: {anchorId}}).catch(() => undefined);
        }
        throw designationErr;
      }
      await Promise.all([refetchAccount(), refetchAnchors()]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to set opening balance',
      );
    }
  };

  const handleClear = async () => {
    setError(null);
    try {
      await setOpeningAnchor({variables: {accountId, anchorId: null}});
      await Promise.all([refetchAccount(), refetchAnchors()]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to clear opening balance',
      );
    }
  };

  return (
    <div className="border-border flex flex-col gap-3 rounded-md border p-3">
      <div className="flex flex-col">
        <span className="font-medium">Opening balance</span>
        <span className="text-muted-foreground text-sm">
          Mark the account&apos;s start: a $0 balance on the date it began,
          below which history is complete. The first deposit is a transaction
          after it. If early history is missing, leave this unset.
        </span>
      </div>
      {!ready ? (
        <span className="text-muted-foreground text-sm">Loading…</span>
      ) : openingAnchor ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm">
            Set to <span className="font-medium">{openingAnchor.date}</span> ·{' '}
            {formatMoney(
              Money.fromServer({
                amount: toDisplayBalance(openingAnchor.balance, isCreditCard),
                currency,
              }),
            )}
          </span>
          <Button
            variant="outline"
            disabled={setting}
            onClick={() => void handleClear()}
          >
            Clear
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <InputGroup label="Start date">
            <DatePicker selectedDate={date} onChange={setDate} />
          </InputGroup>
          <div>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void handleSet()}
            >
              Set opening balance ($0)
            </Button>
          </div>
        </div>
      )}
      {error != null && (
        <span className="text-destructive text-sm">{error}</span>
      )}
    </div>
  );
};

export const EditAccountModal = (
  props: EditAccountModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const {account, onRequestClose} = props;
  const [name, setName] = React.useState(account.name);
  const [depositoryType, setDepositoryType] = React.useState(
    account.depositoryType,
  );
  const [logo, setLogo] = React.useState(account.logo ?? '');
  const [cardProduct, setCardProduct] = React.useState(
    account.cardProduct ?? '',
  );
  const [trackBalance, setTrackBalance] = React.useState(
    account.trackBalance ?? false,
  );
  const [updateAccount, {loading: updateLoading}] = useMutation(
    UpdateDepositoryAccountDocument,
  );
  const [deleteAccount, {loading: deleteLoading}] = useMutation(
    DeleteAccountDocument,
  );
  const [error, setError] = React.useState<string | null>(null);

  const hasChanges =
    name !== account.name ||
    depositoryType !== account.depositoryType ||
    logo !== (account.logo ?? '') ||
    cardProduct !== (account.cardProduct ?? '') ||
    trackBalance !== (account.trackBalance ?? false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    updateAccount({
      variables: {
        id: account._id,
        name,
        depositoryType,
        logo: logo || null,
        // Only sent when touched: a caller whose account shape predates
        // the field would otherwise clear it on every unrelated save
        cardProduct:
          cardProduct !== (account.cardProduct ?? '')
            ? cardProduct || null
            : undefined,
        trackBalance,
      },
    })
      .then(() => {
        props.onUpdate?.();
        onRequestClose();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to save');
      });
  };

  const handleAccountTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {value} = e.target;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    setDepositoryType(value as DepositoryType);
  };

  const handleDelete = () => {
    deleteAccount({
      variables: {accountId: account._id},
      update: (
        store: ApolloCache<object>,
        result: FetchResult<DeleteAccountMutation>,
      ) => {
        const newAccounts = result?.data?.deleteAccount;
        if (newAccounts) {
          setAccounts(store, newAccounts);
        }
      },
    })
      .then(() => {
        props.onDelete?.(account);
        onRequestClose();
      })
      .catch((err: unknown) => {
        // Deletion can be refused server-side (existing transactions, fee
        // periods, an active Plaid connection, …) — surface it instead of
        // failing silently.
        setError(err instanceof Error ? err.message : 'Failed to delete');
      });
  };

  const footer = (
    <>
      <Button
        variant="destructive"
        onClick={handleDelete}
        disabled={deleteLoading}
      >
        Delete
      </Button>
      <Button
        variant="outline"
        onClick={onRequestClose}
        disabled={updateLoading}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={!hasChanges || updateLoading}
      >
        Save
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit account"
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <InputGroup label="Name">
          <TextInput autoFocus value={name} onChange={setName} />
        </InputGroup>
        <InputGroup label="Account type">
          <select value={depositoryType} onChange={handleAccountTypeChange}>
            <option value={DepositoryType.Checking}>Checking</option>
            <option value={DepositoryType.Savings}>Savings</option>
            <option value={DepositoryType.CreditCard}>Credit Card</option>
            <option value={DepositoryType.Cash}>Cash</option>
            <option value={DepositoryType.Wallet}>Wallet</option>
            <option value={DepositoryType.Loan}>Loan</option>
            <option value={DepositoryType.Ledger}>Ledger</option>
            <option value={DepositoryType.Other}>Other</option>
          </select>
        </InputGroup>
        <LogoSelect value={logo} onChange={setLogo} />
        {depositoryType === DepositoryType.CreditCard && (
          <CardProductSelect value={cardProduct} onChange={setCardProduct} />
        )}
        <div
          className="flex cursor-pointer items-center gap-3"
          onClick={() => setTrackBalance(!trackBalance)}
        >
          <Checkbox value={trackBalance} onChange={setTrackBalance} />
          <div className="flex flex-col">
            <span className="font-medium">Track nonzero balance</span>
            <span className="text-muted-foreground text-sm">
              Shows on the inbox while the balance isn&apos;t zero — gift
              cards, credits, loans.
            </span>
          </div>
        </div>
        {error != null && (
          <div className="text-destructive text-sm">{error}</div>
        )}
        <OpeningBalanceSection accountId={account._id} />
        <AccountClosingSection accountId={account._id} />
      </form>
    </Modal>
  );
};
